import { defaultBillingDayOfWeek, getDefaultBillingDayOfYear } from '~/constants';
import { BillingTriggerType } from '~/types/api.model';
import { BillingDayOfMonthModel } from '../BillingDayOfMonth';
import { OneTimeInStagesModel } from '../OneTimeInStagesModel';
import { makeMaxCharges, makeOnApprovalBilling, makeOnDemandBilling, makeOneTimeManualBilling, makeOngoingBilling, makeRepeatableManualBilling } from './makers';
import { RecurrencePeriodTypes } from './model';
import * as selectors from './selectors';
export function fromDTO(dto, isAwaitingApproval, options) {
    const { recurrencePeriod, billingDayOfMonth, billingDayOfWeek, billingDayOfYear, isBilledUpfront, billUpfrontState, isPaused, isProrated, maxCharges } = dto;
    switch(dto.trigger){
        case 'onApproval':
        case BillingTriggerType.OnApproval:
            return makeOnApprovalBilling();
        case BillingTriggerType.OneTimeManual:
            return makeOneTimeManualBilling();
        case BillingTriggerType.RepeatableManual:
            if (recurrencePeriod !== '' && recurrencePeriod != null) return makeRepeatableManualBilling(recurrencePeriod, {
                maxCharges
            });
            else return null;
        case BillingTriggerType.Ongoing:
            if (recurrencePeriod !== '' && recurrencePeriod != null) return makeOngoingBilling(recurrencePeriod, {
                billingDayOfMonth: billingDayOfMonth && BillingDayOfMonthModel.make(billingDayOfMonth),
                billingDayOfWeek,
                billingDayOfYear,
                isBilledUpfront,
                billUpfrontState: billUpfrontState != null ? billUpfrontState : undefined,
                isProrated,
                isPaused,
                maxCharges,
                isAwaitingApproval,
                startBillingAt: options === null || options === void 0 ? void 0 : options.startBillingAt
            });
            else return null;
        case BillingTriggerType.OneTimeInStages:
            return {
                trigger: BillingTriggerType.OneTimeInStages,
                oneTimeInStages: OneTimeInStagesModel.fromDTO(dto.oneTimeInStages)
            };
        case BillingTriggerType.OnDemand:
            return makeOnDemandBilling(dto.onDemand.preapprovalPeriod, {
                maxCharges
            });
    }
}
export function toDTO(billing) {
    switch(billing.trigger){
        case BillingTriggerType.OnApproval:
            return {
                trigger: BillingTriggerType.OnApproval,
                billingDayOfMonth: null,
                billingDayOfWeek: null,
                billingDayOfYear: null,
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                maxCharges: makeMaxCharges(false),
                recurrencePeriod: ''
            };
        case BillingTriggerType.OneTimeManual:
            return {
                trigger: BillingTriggerType.OneTimeManual,
                billingDayOfMonth: null,
                billingDayOfWeek: null,
                billingDayOfYear: null,
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                maxCharges: makeMaxCharges(false),
                recurrencePeriod: ''
            };
        case BillingTriggerType.Ongoing:
            return {
                trigger: BillingTriggerType.Ongoing,
                billingDayOfYear: billing.billingDayOfYear,
                isBilledUpfront: billing.isBilledUpfront,
                billUpfrontState: billing.isBilledUpfront ? billing.billUpfrontState : null,
                recurrencePeriod: billing.recurrencePeriod,
                billingDayOfMonth: billing.billingDayOfMonth ? billing.billingDayOfMonth.toJSON() : null,
                billingDayOfWeek: billing.billingDayOfWeek,
                isProrated: billing.isProrated,
                isPaused: billing.isPaused,
                maxCharges: billing.maxCharges
            };
        case BillingTriggerType.RepeatableManual:
            return {
                trigger: BillingTriggerType.RepeatableManual,
                billingDayOfMonth: billing.billingDayOfMonth ? billing.billingDayOfMonth.toJSON() : null,
                billingDayOfWeek: billing.billingDayOfWeek,
                billingDayOfYear: billing.billingDayOfYear,
                isBilledUpfront: billing.isBilledUpfront,
                recurrencePeriod: billing.recurrencePeriod,
                maxCharges: billing.maxCharges,
                isPaused: false,
                isProrated: false
            };
        case BillingTriggerType.OneTimeInStages:
            return {
                trigger: BillingTriggerType.OneTimeInStages,
                billingDayOfMonth: null,
                billingDayOfWeek: null,
                billingDayOfYear: null,
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                maxCharges: makeMaxCharges(false),
                recurrencePeriod: '',
                oneTimeInStages: billing.oneTimeInStages.toDTO()
            };
        case BillingTriggerType.OnDemand:
            return {
                trigger: BillingTriggerType.OnDemand,
                billingDayOfMonth: null,
                billingDayOfWeek: null,
                billingDayOfYear: null,
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                maxCharges: billing.maxCharges,
                recurrencePeriod: '',
                onDemand: {
                    preapprovalPeriod: billing.preapprovalPeriod
                }
            };
    }
}
export function toDBServiceBillingTrigger(billing) {
    switch(billing.trigger){
        case BillingTriggerType.OnApproval:
            return {
                type: BillingTriggerType.OnApproval
            };
        case BillingTriggerType.OneTimeManual:
            return {
                type: BillingTriggerType.OneTimeManual
            };
        case BillingTriggerType.Ongoing:
            return {
                type: BillingTriggerType.Ongoing,
                ongoing: {
                    billUpfront: billing.isBilledUpfront,
                    billUpfrontState: billing.billUpfrontState,
                    isPaused: billing.isPaused,
                    recurrence: toDBRecurrenceData(billing),
                    prorateFirstInvoice: billing.isProrated,
                    maxCharges: {
                        enabled: selectors.maxChargesEnabled(billing),
                        limit: selectors.maxChargesLimit(billing),
                        chargesLeft: selectors.maxChargesLeft(billing)
                    }
                }
            };
        case BillingTriggerType.RepeatableManual:
            return {
                type: BillingTriggerType.RepeatableManual,
                repeatableManual: {
                    billUpfront: billing.isBilledUpfront,
                    recurrence: toDBRecurrenceData(billing),
                    maxCharges: {
                        enabled: selectors.maxChargesEnabled(billing),
                        limit: selectors.maxChargesLimit(billing),
                        chargesLeft: selectors.maxChargesLeft(billing)
                    }
                }
            };
        case BillingTriggerType.OneTimeInStages:
            return {
                type: BillingTriggerType.OneTimeInStages,
                oneTimeInStages: billing.oneTimeInStages.toDTO()
            };
        case BillingTriggerType.OnDemand:
            return {
                type: BillingTriggerType.OnDemand,
                onDemand: {
                    preapprovalPeriod: billing.preapprovalPeriod,
                    maxCharges: billing.maxCharges
                }
            };
    }
}
function toDBRecurrenceData(billing) {
    const { recurrencePeriod } = billing;
    const billingDayOfMonth = billing.billingDayOfMonth || BillingDayOfMonthModel.make();
    const billingDayOfWeek = billing.billingDayOfWeek || defaultBillingDayOfWeek;
    const billingDayOfYear = billing.billingDayOfYear || getDefaultBillingDayOfYear();
    return {
        period: {
            type: recurrencePeriod,
            monthly: recurrencePeriod === RecurrencePeriodTypes.Monthly ? {
                dayOfMonth: billingDayOfMonth.toDBType()
            } : null,
            weekly: recurrencePeriod === RecurrencePeriodTypes.Weekly ? {
                day: billingDayOfWeek
            } : null,
            biweekly: recurrencePeriod === RecurrencePeriodTypes.BiWeekly ? {
                day: billingDayOfWeek
            } : null,
            yearly: recurrencePeriod === RecurrencePeriodTypes.Yearly ? {
                dayOfYear: billingDayOfYear
            } : null
        }
    };
}
